import React, { useEffect, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { PromiseToPayWithUser, PromiseToPayCreate, BuyerContact } from '@dill/dill-shared';
import { useAppSelector, useAppDispatch } from '../../../../../redux/store';
import {
  getBuyerPromiseToPays,
  createPromiseToPay,
  updatePromiseToPay,
  cancelPromiseToPay,
  deletePromiseToPay,
} from '../../../../../redux/services/promiseToPayService';
import { Plus } from 'react-feather';
import { DillButton } from '../../../../general/Dill';
import PromiseToPaysTable from './PromiseToPaysTable';
import { PromiseToPayFormModal } from './PromiseToPayFormModal';
import { PromiseToPayDetailsModal } from './PromiseToPayDetailsModal';

function SupplierCustomerPromiseToPays({
  buyerId,
  contacts,
}: {
  buyerId: string;
  contacts?: BuyerContact[];
}) {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { buyerPromiseToPays, loadingList } = useAppSelector((state) => state.promiseToPays);

  const promiseIdToView = new URLSearchParams(location.search).get('promiseToPayId') || undefined;

  const [searchText, setSearchText] = useState('');
  const [sortOptions, setSortOptions] = useState<{ sortOrder: 'asc' | 'desc'; sortBy: string }>({
    sortOrder: 'desc',
    sortBy: 'createdAt',
  });
  const [selectedPromise, setSelectedPromise] = useState<PromiseToPayWithUser | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingPromise, setEditingPromise] = useState<PromiseToPayWithUser | null>(null);

  useEffect(() => {
    if (currentSupplier?.id) {
      dispatch(getBuyerPromiseToPays({ supplierId: currentSupplier.id, buyerId }));
    }
  }, [currentSupplier?.id, buyerId, dispatch]);

  useEffect(() => {
    if (promiseIdToView && buyerPromiseToPays.length > 0) {
      const promise = buyerPromiseToPays.find((p) => p.id === promiseIdToView);
      if (promise) {
        setSelectedPromise(promise);
        setIsDetailsModalOpen(true);
      }
    }
  }, [promiseIdToView, buyerPromiseToPays]);

  const handleCreate = async (data: PromiseToPayCreate) => {
    if (currentSupplier?.id) {
      await dispatch(createPromiseToPay({ supplierId: currentSupplier.id, data }));
      setIsFormModalOpen(false);
    }
  };

  const handleCancel = async (promiseToPayId: string) => {
    if (currentSupplier?.id) {
      await dispatch(
        cancelPromiseToPay({
          supplierId: currentSupplier.id,
          promiseToPayId,
        })
      );
      dispatch(getBuyerPromiseToPays({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  const handleDelete = async (promiseToPayId: string) => {
    if (currentSupplier?.id) {
      await dispatch(
        deletePromiseToPay({
          supplierId: currentSupplier.id,
          promiseToPayId,
        })
      );
      dispatch(getBuyerPromiseToPays({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  const handleRowClick = (promise: PromiseToPayWithUser) => {
    setSelectedPromise(promise);
    setIsDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedPromise(null);
    const params = new URLSearchParams(location.search);
    if (params.has('promiseToPayId')) {
      params.delete('promiseToPayId');
      navigate({ search: params.toString() }, { replace: true });
    }
  };

  const handleCloseFormModal = () => {
    setIsFormModalOpen(false);
    setEditingPromise(null);
  };

  const handleEdit = (promise: PromiseToPayWithUser) => {
    setEditingPromise(promise);
    setIsFormModalOpen(true);
  };

  const handleUpdate = async (data: PromiseToPayCreate) => {
    if (currentSupplier?.id && editingPromise) {
      await dispatch(
        updatePromiseToPay({
          supplierId: currentSupplier.id,
          promiseToPayId: editingPromise.id,
          data,
        })
      );
      setIsFormModalOpen(false);
      setEditingPromise(null);
      dispatch(getBuyerPromiseToPays({ supplierId: currentSupplier.id, buyerId }));
    }
  };

  const isLoading = loadingList.includes('getBuyerPromiseToPays');

  const filteredPromiseToPays = buyerPromiseToPays
    .filter((promise) => {
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        return (
          (promise.source || '').toLowerCase().includes(searchLower) ||
          (promise.type || '').toLowerCase().includes(searchLower) ||
          (promise.reason || '').toLowerCase().includes(searchLower) ||
          promise.status?.toLowerCase().includes(searchLower) ||
          (promise.createdByUser?.firstName || '').toLowerCase().includes(searchLower) ||
          (promise.createdByUser?.lastName || '').toLowerCase().includes(searchLower) ||
          (promise.createdByUser?.email || '').toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .sort((a, b) => {
      let valueA: string | number | boolean = 0;
      let valueB: string | number | boolean = 0;

      if (
        sortOptions.sortBy === 'promisedPaymentDate' ||
        sortOptions.sortBy === 'createdAt' ||
        sortOptions.sortBy === 'updatedAt'
      ) {
        valueA = a[sortOptions.sortBy as keyof PromiseToPayWithUser]
          ? new Date(a[sortOptions.sortBy as keyof PromiseToPayWithUser] as Date).getTime()
          : 0;
        valueB = b[sortOptions.sortBy as keyof PromiseToPayWithUser]
          ? new Date(b[sortOptions.sortBy as keyof PromiseToPayWithUser] as Date).getTime()
          : 0;
      } else if (sortOptions.sortBy === 'amount') {
        valueA = a.amount || 0;
        valueB = b.amount || 0;
      } else {
        valueA = (a[sortOptions.sortBy as keyof PromiseToPayWithUser] as string) || '';
        valueB = (b[sortOptions.sortBy as keyof PromiseToPayWithUser] as string) || '';
      }

      if (valueA < valueB) return sortOptions.sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOptions.sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div className="w-full h-full pt-4">
      <div className="flex flex-col overflow-x-scroll h-full w-full">
        <PromiseToPaysTable
          promiseToPays={filteredPromiseToPays}
          sortOptions={sortOptions}
          onSortChange={setSortOptions}
          onCancel={handleCancel}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onRowClick={handleRowClick}
          searchValue={searchText}
          onSearch={setSearchText}
          toolbarRight={
            <DillButton
              label="New Promise"
              icon={<Plus size={14} />}
              onClick={() => setIsFormModalOpen(true)}
              height={31}
            />
          }
        />
      </div>

      <PromiseToPayDetailsModal
        open={isDetailsModalOpen}
        promise={selectedPromise}
        onClose={handleCloseDetailsModal}
        onCancel={handleCancel}
      />

      <PromiseToPayFormModal
        open={isFormModalOpen}
        onClose={handleCloseFormModal}
        onSubmit={editingPromise ? handleUpdate : handleCreate}
        initialData={editingPromise}
        isLoading={
          loadingList.includes('createPromiseToPay') || loadingList.includes('updatePromiseToPay')
        }
        supplierId={currentSupplier?.id || ''}
        buyerId={buyerId}
        contacts={contacts}
      />
    </div>
  );
}

export default SupplierCustomerPromiseToPays;
